'use client'

import { useTranslations } from 'next-intl'
import { useEffect, useRef } from 'react'

import { useSnackbarControls } from '~/store/slices/display'

import { pendingDraftKey, subscribePendingDraftStashed, takePendingDraft } from './pendingDraft'
import type { BillingComposerEntity, BillingComposerMode } from './types'
import { useComposerFormAdapter } from './useComposerFormAdapter'
import useSessionScope from './useSessionScope'

type PendingDraftApplierProps = {
  entity?: BillingComposerEntity
  mode: BillingComposerMode
  entityId?: string
}

/**
 * Applies a draft stashed by another surface (detail page, billing hub, the global assistant bar) to
 * this wizard's form. Reads sessionStorage once after mount (so a cross-page "Review in form" lands
 * even when the composer panel is closed), and again when a matching draft is stashed while this page
 * is already open — App Router `router.push` to the current URL does not remount.
 */
export default function PendingDraftApplier({ entity, mode, entityId }: PendingDraftApplierProps) {
  const t = useTranslations('features.BillingComposer')
  const { openSnackbar } = useSnackbarControls()
  const adapter = useComposerFormAdapter(entity, mode)
  const sessionScope = useSessionScope()
  const appliedOnMountRef = useRef(false)

  useEffect(() => {
    if (!adapter.hasForm || !entity || !sessionScope) return

    const key = pendingDraftKey(entity, mode === 'edit' ? entityId : undefined, sessionScope)

    const applyPending = () => {
      const pending = takePendingDraft(key)
      if (!pending) return
      const result = adapter.applyDraft(pending.draft, pending.selectedPaths)
      if (!result) return
      openSnackbar(t('pending_applied', { count: result.applied.length, skipped: result.skipped.length }), {
        severity: result.skipped.length > 0 ? 'warning' : 'success'
      })
    }

    // The wizard's own mount effects reset the form from the API response after this (child) effect
    // runs; deferring one tick lets the draft land on top of that reset instead of under it.
    const timer = window.setTimeout(() => {
      if (appliedOnMountRef.current) return
      appliedOnMountRef.current = true
      applyPending()
    }, 0)

    const unsubscribe = subscribePendingDraftStashed((stashedKey) => {
      if (stashedKey !== key) return
      applyPending()
    })

    return () => {
      window.clearTimeout(timer)
      unsubscribe()
    }
  }, [adapter, entity, entityId, mode, openSnackbar, sessionScope, t])

  return null
}
